import { useEffect, useRef, useState, type ReactNode } from 'react';
import { NavLink, useNavigate } from 'react-router';
import { LogOut, Menu, X } from 'lucide-react';
import { signOut, useSession } from '@/lib/auth-client';
import { admin } from '@/lib/api';
import { Wordmark } from './Mark';

interface DashboardLayoutProps {
  /**
   * Page title rendered as a display-style H1 above the content. Omit
   * when the page wants to roll its own header (e.g. PageEditor with
   * inline name editing).
   */
  title?: string;
  /** Optional slot rendered to the right of the title (CTA, status). */
  titleAction?: ReactNode;
  children: ReactNode;
  /**
   * Use a wider content max-width — for the page editor's three-pane
   * canvas layout. Defaults to the standard reading-width max-w-6xl.
   */
  wide?: boolean;
}

/**
 * DashboardLayout — the chrome of every logged-in page.
 *
 * Sticky glass header strip across the top: logo + nav + avatar menu.
 * Content scrolls under it. The body's static gradient mesh handles
 * the backdrop so the header glass has something to refract.
 *
 * `title` is supported for prop-compatibility with existing pages but
 * the layout doesn't render it — every page sets its own H1 inside the
 * content area for finer typographic control.
 */
export function DashboardLayout({ title, titleAction, children, wide }: DashboardLayoutProps) {
  const navigate = useNavigate();
  const { data: session, isPending } = useSession();
  // Cheap session-cached admin check — the endpoint is sub-10ms and we
  // only render the result, never trust it for authorization (every
  // admin endpoint re-checks server-side via requireAdmin).
  const [isAdmin, setIsAdmin] = useState(false);
  useEffect(() => {
    if (!session) return;
    admin.status().then((r) => setIsAdmin(r.isAdmin)).catch(() => setIsAdmin(false));
  }, [session]);

  if (isPending) {
    return (
      <main className="mx-auto flex min-h-screen items-center justify-center text-[var(--color-on-surface-variant)]">
        <div
          className="h-10 w-10 animate-spin rounded-full border-2 border-[var(--color-border)] border-t-[var(--color-primary)]"
          aria-hidden
        />
      </main>
    );
  }
  if (!session) {
    navigate('/login', { replace: true });
    return null;
  }

  async function onSignOut() {
    await signOut();
    navigate('/login');
  }

  const userEmail = session.user.email;
  const initials = (session.user.name || userEmail || 'P').slice(0, 2).toUpperCase();

  return (
    <div className="min-h-screen">
      <Header
        userEmail={userEmail}
        initials={initials}
        isAdmin={isAdmin}
        onSignOut={onSignOut}
      />
      {/* Tighter horizontal padding on mobile (16px) than desktop (24px) —
          gives narrow screens an extra ~16px of usable content width. */}
      <main className={`mx-auto px-4 py-6 sm:px-6 sm:py-8 ${wide ? 'max-w-[1440px]' : 'max-w-6xl'}`}>
        {title && (
          <div className="mb-7 flex flex-wrap items-end justify-between gap-4">
            <h1 className="font-display text-3xl font-bold tracking-tight md:text-4xl">
              {title}
            </h1>
            {titleAction && <div className="shrink-0">{titleAction}</div>}
          </div>
        )}
        {children}
      </main>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Header — sticky glass strip with logo, nav, and user menu.
// ---------------------------------------------------------------------------

function Header({
  userEmail,
  initials,
  isAdmin,
  onSignOut,
}: {
  userEmail: string;
  initials: string;
  isAdmin: boolean;
  onSignOut: () => void;
}) {
  return (
    <header
      className="sticky top-0 z-30 border-b border-[var(--color-border)] backdrop-blur-xl"
      style={{ background: 'rgba(19, 19, 19, 0.55)' }}
    >
      <div className="mx-auto flex max-w-[1440px] items-center gap-3 px-4 py-3 sm:gap-4 sm:px-6">
        <Wordmark to="/app" size="sm" />

        {/* Desktop nav — visible md+. Below md the 6 tabs wrap to two
            lines and look broken; the hamburger menu (right side) takes
            over. */}
        <nav className="ml-2 hidden flex-wrap items-center gap-1 text-sm md:flex">
          <NavTab to="/app">Overview</NavTab>
          <NavTab to="/app/pages">Pages</NavTab>
          <NavTab to="/app/domains">Domains</NavTab>
          <NavTab to="/app/analytics">Analytics</NavTab>
          <NavTab to="/app/settings/members">Team</NavTab>
          {isAdmin && <NavTab to="/app/admin/clients">Admin</NavTab>}
        </nav>

        <div className="ml-auto flex items-center gap-2">
          <MobileNav isAdmin={isAdmin} />
          <UserMenu userEmail={userEmail} initials={initials} onSignOut={onSignOut} />
        </div>
      </div>
    </header>
  );
}

/**
 * Hamburger menu for narrow viewports. Same NavTab set as the desktop
 * nav, just stacked vertically inside a glass dropdown. Hidden md+
 * where the inline nav takes over. Closes on outside click, ESC, or
 * tapping any nav item (NavLink's `onClick` doesn't fire after route
 * change, so we close inside the click handler too).
 */
function MobileNav({ isAdmin }: { isAdmin: boolean }) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    function onDoc(e: MouseEvent) {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  function close() {
    setOpen(false);
  }

  return (
    <div ref={rootRef} className="relative md:hidden">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label="Menu"
        aria-expanded={open}
        className="flex h-9 w-9 items-center justify-center rounded-full border border-[var(--color-border)] text-[var(--color-on-surface-variant)] transition hover:bg-[var(--surface-glass)] hover:text-[var(--color-foreground)]"
      >
        {open ? <X size={18} /> : <Menu size={18} />}
      </button>
      {open && (
        <div
          className="glass-strong absolute right-0 top-12 z-40 w-56 rounded-2xl p-2"
          style={{
            boxShadow:
              '0 1px 0 0 rgba(255,255,255,0.04) inset, 0 24px 48px -16px rgba(0,0,0,0.6)',
          }}
        >
          <nav className="flex flex-col gap-0.5">
            <MobileNavLink to="/app" onClose={close}>Overview</MobileNavLink>
            <MobileNavLink to="/app/pages" onClose={close}>Pages</MobileNavLink>
            <MobileNavLink to="/app/domains" onClose={close}>Domains</MobileNavLink>
            <MobileNavLink to="/app/analytics" onClose={close}>Analytics</MobileNavLink>
            <MobileNavLink to="/app/settings/members" onClose={close}>Team</MobileNavLink>
            {isAdmin && (
              <MobileNavLink to="/app/admin/clients" onClose={close}>
                Admin
              </MobileNavLink>
            )}
          </nav>
        </div>
      )}
    </div>
  );
}

function MobileNavLink({
  to,
  onClose,
  children,
}: {
  to: string;
  onClose: () => void;
  children: ReactNode;
}) {
  return (
    <NavLink
      to={to}
      end
      onClick={onClose}
      className={({ isActive }) =>
        `block rounded-xl px-3 py-2 text-sm font-medium transition ${
          isActive
            ? 'bg-[var(--color-primary)] text-[var(--color-primary-foreground)]'
            : 'text-[var(--color-on-surface-variant)] hover:bg-[var(--surface-glass)] hover:text-[var(--color-foreground)]'
        }`
      }
    >
      {children}
    </NavLink>
  );
}

function NavTab({
  to,
  children,
  disabled,
}: {
  to: string;
  children: ReactNode;
  disabled?: boolean;
}) {
  if (disabled) {
    return (
      <span
        title="Coming soon"
        className="cursor-not-allowed rounded-full px-3 py-1.5 text-[var(--color-on-surface-variant)] opacity-50"
      >
        {children}
      </span>
    );
  }
  return (
    <NavLink
      to={to}
      end
      className={({ isActive }) =>
        `relative rounded-full px-3.5 py-1.5 font-medium transition ${
          isActive
            ? 'bg-[var(--color-primary)] text-[var(--color-primary-foreground)]'
            : 'text-[var(--color-on-surface-variant)] hover:bg-[var(--surface-glass)] hover:text-[var(--color-foreground)]'
        }`
      }
    >
      {children}
    </NavLink>
  );
}

// User menu — avatar circle, click → glass dropdown. Closes on outside
// click or ESC. Kept lightweight (no Radix dependency).
function UserMenu({
  userEmail,
  initials,
  onSignOut,
}: {
  userEmail: string;
  initials: string;
  onSignOut: () => void;
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    function onDoc(e: MouseEvent) {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label="Account menu"
        aria-expanded={open}
        className="flex h-9 w-9 items-center justify-center rounded-full font-display text-xs font-bold transition hover:brightness-110"
        style={{
          background: 'linear-gradient(135deg, #cdbdff 0%, #7c4dff 100%)',
          color: '#370096',
        }}
      >
        {initials}
      </button>
      {open && (
        <div
          className="glass-strong absolute right-0 top-12 z-40 w-64 rounded-2xl p-2"
          style={{
            boxShadow:
              '0 1px 0 0 rgba(255,255,255,0.04) inset, 0 24px 48px -16px rgba(0,0,0,0.6)',
          }}
        >
          <div className="border-b border-[var(--color-border)] px-3 py-2.5">
            <div className="text-xs font-semibold uppercase tracking-[0.06em] text-[var(--color-on-surface-variant)]">
              Signed in
            </div>
            <div className="mt-0.5 truncate font-mono text-xs text-[var(--color-foreground)]">
              {userEmail}
            </div>
          </div>
          <button
            type="button"
            onClick={onSignOut}
            className="mt-1 flex w-full items-center gap-2 rounded-xl px-3 py-2 text-left text-sm font-medium text-[var(--color-on-surface-variant)] transition hover:bg-[var(--surface-glass)] hover:text-[var(--color-foreground)]"
          >
            <LogOut size={14} /> Sign out
          </button>
        </div>
      )}
    </div>
  );
}
